// Grading Pipeline widget — the second card on AdminOverviewPage. Shows
// overall progress bar + per-domain rows.
import React, { useState } from 'react';
import { Brain, RefreshCw, CheckCircle2 } from 'lucide-react';
import { DomainProgressRow } from './DomainProgressRow';
import { overallBarColor } from './overviewHelpers';
import { GradingStatusBreakdownModal } from './GradingStatusBreakdownModal';
import { useAuth } from '../../../contexts/AuthContext';

const REFRESH_INTERVAL = 30;

const WidgetBadge = ({ domainsGrading, totalGradeable, totalGraded }) => {
  if (domainsGrading > 0) {
    return (
      <span className="flex items-center gap-1.5 text-xs px-2.5 py-0.5 rounded-full bg-teal-500/15 text-teal-300 border border-teal-500/25">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" />
        </span>
        {domainsGrading} domain{domainsGrading > 1 ? 's' : ''} grading
      </span>
    );
  }
  if (totalGradeable === 0 && totalGraded > 0) {
    return (
      <span className="flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
        <CheckCircle2 className="w-3 h-3" /> All caught up
      </span>
    );
  }
  return null;
};

const PipelineSummary = ({ totalGraded, totalCalls, overallPct, domainsGrading, pendingTotal, totalGradeable }) => {
  const segments = [
    { label: 'Graded', value: totalGraded, color: 'text-teal-400' },
    { label: 'Pending', value: pendingTotal, color: 'text-amber-400' },
    { label: 'Gradeable now', value: totalGradeable, color: totalGradeable > 0 ? 'text-teal-300' : 'text-white/20' },
  ];
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-white/40 text-xs">Overall progress</span>
        <span className="text-white/60 text-xs font-medium">
          {totalGraded.toLocaleString()} / {totalCalls.toLocaleString()} calls graded ({overallPct}%)
        </span>
      </div>
      <div className="h-2.5 bg-white/8 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ${
            overallBarColor(overallPct, domainsGrading)
          } ${domainsGrading > 0 ? 'animate-pulse' : ''}`}
          style={{ width: `${overallPct}%` }}
        />
      </div>
      <div className="flex items-center gap-4 mt-2">
        {segments.map(s => (
          <div key={s.label} className="flex items-center gap-1.5">
            <span className={`text-sm font-semibold ${s.color}`}>{s.value.toLocaleString()}</span>
            <span className="text-white/25 text-xs">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const WINDOW_OPTIONS = [
  { key: null,  label: 'All time' },
  { key: 7,     label: '7d' },
  { key: 30,    label: '30d' },
  { key: 90,    label: '90d' },
  { key: 180,   label: '6mo' },
  { key: 365,   label: '12mo' },
];

export const GradingPipelineWidget = ({
  domains, totals, lastRefreshed, countdown, refreshing, onRefresh, onRowClick,
  windowDays, onWindowChange,
}) => {
  const { totalCalls, totalGraded, totalGradeable, domainsGrading, overallPct, pendingTotal } = totals;
  const { token } = useAuth();
  const [drillDomainId, setDrillDomainId] = useState(null);
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid="grading-pipeline-widget">
      <div className="px-6 py-4 border-b border-white/5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-teal-400" />
              <h2 className="text-white font-semibold">Grading Pipeline</h2>
            </div>
            <WidgetBadge
              domainsGrading={domainsGrading}
              totalGradeable={totalGradeable}
              totalGraded={totalGraded}
            />
          </div>
          <div className="flex items-center gap-3">
            {/* Window filter chips */}
            <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5" data-testid="grading-window-filter">
              {WINDOW_OPTIONS.map(w => (
                <button
                  key={w.label}
                  onClick={() => onWindowChange && onWindowChange(w.key)}
                  className={`text-[11px] font-semibold px-2 py-1 rounded ${
                    (windowDays ?? null) === w.key
                      ? 'bg-teal-500/25 text-teal-200 border border-teal-500/40'
                      : 'text-white/40 hover:text-white/70 border border-transparent'
                  }`}
                  data-testid={`grading-window-${w.label}`}
                >
                  {w.label}
                </button>
              ))}
            </div>
            <span className="text-white/20 text-xs">
              {lastRefreshed ? `refreshes in ${countdown}s` : 'Loading...'}
            </span>
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors"
              title="Refresh now"
              data-testid="refresh-pipeline-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        <PipelineSummary
          totalGraded={totalGraded} totalCalls={totalCalls} overallPct={overallPct}
          domainsGrading={domainsGrading} pendingTotal={pendingTotal}
          totalGradeable={totalGradeable}
        />
      </div>

      <div className="px-5 py-2">
        {domains.length === 0 ? (
          <p className="text-white/20 text-sm text-center py-6">No domains configured</p>
        ) : (
          domains.map(d => (
            <DomainProgressRow
              key={d.domain_id}
              d={d}
              onClick={() => setDrillDomainId(d.domain_id)}
              onDetailClick={() => onRowClick(d.domain_id)}
            />
          ))
        )}
      </div>

      <div className="px-6 py-3 border-t border-white/5 flex items-center justify-between">
        <p className="text-white/15 text-xs">Click a row for status breakdown · use the arrow icon for full domain detail</p>
        <p className="text-white/15 text-xs">Auto-refreshes every {REFRESH_INTERVAL}s</p>
      </div>

      {drillDomainId && (
        <GradingStatusBreakdownModal
          token={token}
          domainId={drillDomainId}
          windowDays={windowDays}
          onClose={() => setDrillDomainId(null)}
        />
      )}
    </div>
  );
};
